import type { InvoiceItem, MenuItem } from "@/lib/api/types";

/**
 * T11 — WARN AT FIRE, NEVER BLOCK (D3: stock never blocks a sale).
 *
 * A dish can be 86'd between the moment a waiter puts it in the basket and the
 * moment the basket is sent: `addItems` refuses an 86'd dish, `fireItems` does
 * not re-check (and must not — the server's refusal would be a block). So the
 * till asks, once, before it sends: «نفد «{item}» بعد إضافته. هل تريد الإرسال
 * رغم ذلك؟» and either answer is the person's.
 *
 * Returns the distinct dishes among `drafts` the menu read now marks out
 * (`isAvailable === false`, whatever `unavailableBecause` says — a combo whose
 * component ran out cannot be plated either), in basket order, one entry per
 * dish however many plates of it are in the basket. A dish the menu read does
 * not list (an API mid-deploy, a dish taken off the catalogue) is NOT reported:
 * the warning names what the kitchen marked, never a guess.
 *
 * Only DRAFT rows count: a plate already fired is the kitchen's already, and a
 * PENDING diner request is accepted through its own door.
 */
export type OutOfStockDraft = {
  productId: string;
  nameAr: string;
  nameEn: string | null;
  /** How many plates of it the basket holds. */
  count: number;
};

export function outOfStockDrafts(
  drafts: readonly Pick<InvoiceItem, "productId" | "nameAr" | "nameEn" | "fulfillmentState">[],
  menuItems: readonly Pick<MenuItem, "id" | "isAvailable">[] | undefined,
): OutOfStockDraft[] {
  if (!menuItems || menuItems.length === 0) return [];
  const out = new Set(
    menuItems.filter((item) => item.isAvailable === false).map((item) => item.id),
  );
  if (out.size === 0) return [];
  const found = new Map<string, OutOfStockDraft>();
  for (const draft of drafts) {
    if (draft.fulfillmentState !== "DRAFT" || !out.has(draft.productId)) continue;
    const seen = found.get(draft.productId);
    if (seen) seen.count += 1;
    else
      found.set(draft.productId, {
        productId: draft.productId,
        nameAr: draft.nameAr,
        nameEn: draft.nameEn,
        count: 1,
      });
  }
  return [...found.values()];
}
